import { BrokerInactiveNotice } from '@/features/brokers/components/broker-inactive-notice.component';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { usePmeDocusignSignaturesStatus } from '@/features/proposals/hooks/pme/use-pme-docusign-signatures.hook';
import { useRequirePermissions } from '@/shared/hooks';
import {
  canCloneGroupLifeProposal,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';
import { Page, PageHeader } from '@/shared/ui/page';
import { toaster } from '@/shared/ui/toaster.component';
import { GroupLifeClonePendingError } from '@/shared/utils/group-life-clone-idempotency.util';
import { Button, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Copy, Home } from 'lucide-react';
import { useState } from 'react';
import {
  useCloneGroupLifeProposal,
  useGroupLifeProposalDetails,
} from '../../hooks/group-life';
import { CloneProposalDialog } from './clone-proposal.dialog';
import { PmeProposalWizard } from './proposal-wizard.component';

type Props = {
  proposalId: string;
  mode: 'edit' | 'view';
  startStep?: 'insurance' | 'resume';
};

export function PmeProposalPage({ proposalId, mode, startStep }: Props) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGroupLifeProposalDetails(proposalId);
  const proposalActionGuard = useSelectedBrokerWriteGuard('proposal', 'other');
  const { hasPermission: canWrite } = useRequirePermissions({
    permissions: ['policies.proposals.write'],
  });
  const signaturesQuery = usePmeDocusignSignaturesStatus(proposalId);
  const cloneProposal = useCloneGroupLifeProposal();
  const [isCloneOpen, setIsCloneOpen] = useState(false);

  const signaturesPending =
    Number(data?.statusPortal) === PortalStatus.ProposalInSignature &&
    signaturesQuery.isLoading;
  const showClone =
    canWrite === true &&
    !signaturesPending &&
    canCloneGroupLifeProposal(data?.statusPortal, {
      allSignaturesCompleted: signaturesQuery.data?.allCompleted === true,
    });

  const handleCloneConfirm = () => {
    if (cloneProposal.isPending) return;
    cloneProposal.mutate(
      { policyId: data?.policyId || proposalId },
      {
        onSuccess: cloned => {
          setIsCloneOpen(false);
          toaster.create({
            type: 'success',
            title: 'Proposta clonada',
            description: `Nova proposta Nº ${cloned.proposalNumber} criada em digitação. O plano da clonada é bloqueado.`,
          });
          void navigate({
            to: '/proposals/$id/pme/edit',
            params: { id: cloned.policyId },
          });
        },
        onError: cloneError => {
          if (cloneError instanceof GroupLifeClonePendingError) {
            setIsCloneOpen(false);
            toaster.create({
              type: 'info',
              title: 'Clonar proposta',
              description: cloneError.message,
            });
            return;
          }
          toaster.create({
            type: 'error',
            title: 'Não foi possível clonar',
            description:
              cloneError instanceof Error
                ? cloneError.message
                : 'Não foi possível clonar a proposta. Tente novamente.',
          });
        },
      }
    );
  };

  return (
    <Page.Root>
      <Page.Breadcrumb
        items={[
          {
            label: 'Página Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Comercial' },
          { label: 'Propostas', href: '/proposals' },
          { label: 'Proposta', active: true },
        ]}
      />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>
              {data?.proposalNumber
                ? `Proposta PME - Nº ${data.proposalNumber}`
                : 'Proposta PME'}
            </PageHeader.Title>
            <PageHeader.Subtitle>
              Preencha as informações para efetuar a Proposta
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          {showClone && (
            <PageHeader.Actions>
              <div
                title={proposalActionGuard.reason}
                className='flex flex-col items-end gap-1'
              >
                <Button
                  variant='ghost'
                  size='large'
                  loading={cloneProposal.isPending}
                  disabled={
                    cloneProposal.isPending || proposalActionGuard.blocked
                  }
                  onClick={() => setIsCloneOpen(true)}
                >
                  <Copy className='h-5 w-5' strokeWidth={1.6} />
                  Clonar
                </Button>
              </div>
            </PageHeader.Actions>
          )}
        </PageHeader.Main>
      </PageHeader.Root>

      <BrokerInactiveNotice guards={[proposalActionGuard]} />

      <Page.Content className='py-4'>
        {isLoading ? (
          <div className='h-96 animate-pulse rounded-2xl bg-alba-gray-200' />
        ) : error || !data ? (
          <Text className='text-sm text-alba-error-red-500'>
            Não foi possível carregar a proposta PME. Tente novamente.
          </Text>
        ) : (
          <PmeProposalWizard
            policyId={data.policyId || proposalId}
            productCode={data.productCode}
            proposalNumber={data.proposalNumber}
            details={data}
            mode={mode}
            startStep={startStep}
          />
        )}
      </Page.Content>
      <CloneProposalDialog
        isOpen={isCloneOpen}
        isLoading={cloneProposal.isPending}
        onClose={() => {
          if (cloneProposal.isPending) return;
          setIsCloneOpen(false);
        }}
        onConfirm={handleCloneConfirm}
      />
    </Page.Root>
  );
}
